import { ArrowRight, Loader2, MonitorUp } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { PermissionButton } from '@/components/patterns/permission-button';
import { StatusIcone } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { BuildDetalhe, Painel, StatusBuild } from '@/data/types';
import { useSessao } from '@/hooks/use-sessao';
import { usePodeVer } from '@/hooks/use-acesso';
import { acoesDeStatus, bloqueioPublicacao, ROTULO_STATUS, type AcaoStatus } from '@/lib/capabilities';
import { emitir } from '@/lib/eventos';
import { useAvisoHomologacao } from '@/features/incidentes/aviso-homologacao';
import { aplicarStatus } from './aplicar-status';
import { formatarDataHora } from '@/lib/format';

/** Mensagem de erro de uma escrita: a da API (409/422) quando houver, senão a genérica do cliente HTTP. */
function textoDoErro(causa: unknown): string {
  return mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa));
}

/**
 * Ações sobre uma versão: status de homologação (`maintainer` ou `admin`) e publicação no
 * painel do cliente (`painel`). Sem a capability o botão continua visível,
 * bloqueado, com o motivo no tooltip (padrão `PermissionButton` do PAN).
 */
export function AcoesBuild({
  build,
  aoAtualizar,
}: {
  build: BuildDetalhe;
  aoAtualizar: (detalhe: BuildDetalhe) => void;
}) {
  const { roles } = useSessao();
  const acoes = acoesDeStatus(build, roles);
  const publicacao = bloqueioPublicacao(build, roles);
  const verPainel = usePodeVer('painel');

  const [executando, setExecutando] = React.useState<StatusBuild | null>(null);
  const [erro, setErro] = React.useState<string | null>(null);
  const [acaoComMotivo, setAcaoComMotivo] = React.useState<AcaoStatus | null>(null);
  const [publicarAberto, setPublicarAberto] = React.useState(false);
  const aviso = useAvisoHomologacao();

  async function executar(acao: AcaoStatus) {
    if (acao.exigeMotivo) {
      setAcaoComMotivo(acao);
      return;
    }
    // Homologar versão com incidente aberto/mitigado não é bloqueado, mas exige confirmação.
    if (acao.destino === 'homologado') {
      const confirmado = await aviso.confirmar([
        { versao: build.versao, produto: build.produto, incidentes: build.incidentes },
      ]);
      if (!confirmado) return;
    }
    setExecutando(acao.destino);
    setErro(null);
    try {
      const detalhe = await aplicarStatus(build, acao.destino, null);
      aoAtualizar(detalhe);
      toast.success(`Versão ${build.versao} marcada como ${ROTULO_STATUS[acao.destino].toLowerCase()}.`);
    } catch (causa) {
      setErro(textoDoErro(causa));
    } finally {
      setExecutando(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Ações da versão">
        {acoes.map((acao) => (
          <PermissionButton
            key={acao.destino}
            permitido={acao.bloqueio.permitido}
            motivo={acao.bloqueio.motivo}
            variant={acao.destino === 'homologado' ? 'primary' : 'outline'}
            size="sm"
            className={acao.destino === 'reprovado' && acao.bloqueio.permitido ? 'text-danger' : undefined}
            loading={executando === acao.destino}
            disabled={executando !== null}
            onClick={() => void executar(acao)}
          >
            {acao.bloqueio.permitido && executando !== acao.destino ? <StatusIcone status={acao.destino} /> : null}
            {acao.rotulo}
          </PermissionButton>
        ))}

        {verPainel ? (
          <>
            <span className="mx-1 hidden h-6 w-px bg-border sm:inline-block" aria-hidden="true" />

            <PermissionButton
              permitido={publicacao.permitido}
              motivo={publicacao.motivo}
              size="sm"
              onClick={() => setPublicarAberto(true)}
            >
              <MonitorUp aria-hidden="true" />
              Publicar no painel do cliente
            </PermissionButton>
          </>
        ) : null}
      </div>

      {erro ? (
        <Callout
          tom="erro"
          acao={
            <Button size="sm" variant="ghost" onClick={() => setErro(null)}>
              Dispensar
            </Button>
          }
        >
          {erro}
        </Callout>
      ) : null}

      {aviso.dialogo}

      <MotivoDialog
        build={build}
        acao={acaoComMotivo}
        aoFechar={() => setAcaoComMotivo(null)}
        aoConcluir={(detalhe) => {
          aoAtualizar(detalhe);
          setAcaoComMotivo(null);
        }}
      />

      <PublicarPainelDialog
        aberto={publicarAberto}
        versao={build.versao}
        aoFechar={() => setPublicarAberto(false)}
        aoConcluir={async () => {
          setPublicarAberto(false);
          // O selo "No painel" e o histórico de eventos mudam: relê o detalhe.
          aoAtualizar(await dataSource.obterBuild(build.versao, build.produto));
        }}
      />
    </div>
  );
}

/** Dialog de "reprovar" (rejeição ou demoção): o motivo é obrigatório (a API devolve 422 sem ele). */
export function MotivoDialog({
  build,
  acao,
  aoFechar,
  aoConcluir,
}: {
  build: Pick<BuildDetalhe, 'versao' | 'produto'>;
  acao: Pick<AcaoStatus, 'destino' | 'rotulo' | 'democao'> | null;
  aoFechar: () => void;
  aoConcluir: (detalhe: BuildDetalhe) => void;
}) {
  const [motivo, setMotivo] = React.useState('');
  const [enviando, setEnviando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (acao) {
      setMotivo('');
      setErro(null);
    }
  }, [acao]);

  async function confirmar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!acao || !motivo.trim()) return;
    setEnviando(true);
    setErro(null);
    try {
      const detalhe = await aplicarStatus(build, acao.destino, motivo);
      toast.success(`Versão ${build.versao} marcada como ${ROTULO_STATUS[acao.destino].toLowerCase()}.`);
      aoConcluir(detalhe);
    } catch (causa) {
      setErro(textoDoErro(causa));
    } finally {
      setEnviando(false);
    }
  }

  const rotulo = acao ? ROTULO_STATUS[acao.destino].toLowerCase() : '';

  return (
    <Dialog open={acao !== null} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <DialogContent>
        <form onSubmit={confirmar} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              {acao?.democao ? `Reprovar ${build.versao} (demoção)` : `${acao?.rotulo} ${build.versao}`}
            </DialogTitle>
            <DialogDescription>
              {acao?.democao
                ? 'Esta versão está homologada. Reprová-la é uma demoção: ela deixa de ser homologada e não poderá mais ser publicada no painel do cliente. '
                : ''}
              O motivo fica registrado na linha do tempo da versão e aparece para quem consulta o feed.
            </DialogDescription>
          </DialogHeader>

          <Field
            id="motivo-status"
            label="Motivo"
            obrigatorio
            apoio={
              acao?.democao
                ? 'Por que a versão homologada está sendo demovida?'
                : `Por que a versão está sendo marcada como ${rotulo}?`
            }
          >
            {(props) => (
              <Textarea
                {...props}
                autoFocus
                required
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                placeholder="Ex.: cálculo do DIFAL divergente no Scritta."
              />
            )}
          </Field>

          {erro ? <Callout tom="erro">{erro}</Callout> : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={acao?.destino === 'reprovado' ? 'danger' : 'primary'}
              loading={enviando}
              disabled={!motivo.trim()}
            >
              {acao?.rotulo}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirmação de publicação no painel: mostra a troca que vai acontecer
 * (versão atual -> nova), lida do `GET /v1/painel` no momento em que abre.
 */
export function PublicarPainelDialog({
  aberto,
  versao,
  aoFechar,
  aoConcluir,
}: {
  aberto: boolean;
  versao: string | null;
  aoFechar: () => void;
  aoConcluir: (painel: Painel) => void;
}) {
  const [painel, setPainel] = React.useState<Painel | null>(null);
  const [carregando, setCarregando] = React.useState(false);
  const [enviando, setEnviando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!aberto) return;
    const controller = new AbortController();
    setErro(null);
    setPainel(null);
    setCarregando(true);
    dataSource
      .obterPainel(controller.signal)
      .then(setPainel)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        setErro(textoDoErro(causa));
      })
      .finally(() => setCarregando(false));
    return () => controller.abort();
  }, [aberto]);

  async function confirmar() {
    if (!versao) return;
    setEnviando(true);
    setErro(null);
    try {
      const resultado = await dataSource.publicarPainel(versao);
      emitir({ tipo: 'painel', versao });
      toast.success(`Versão ${versao} publicada no painel do cliente.`);
      aoConcluir(resultado);
    } catch (causa) {
      setErro(textoDoErro(causa));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publicar no painel do cliente?</DialogTitle>
          <DialogDescription>
            A versão publicada passa a ser a versão estável recomendada que os clientes veem no painel e no PAN. Ao
            publicar, o Compilações atualiza o título, os links e o texto do painel e grava o tamanho real dos
            instaladores, lido do CDN.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 rounded-input border border-border bg-bg-subtle px-4 py-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="cmp-eyebrow">Atual</span>
            {carregando ? (
              <span className="flex items-center gap-1.5 text-body text-on-surface-muted">
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Lendo o painel…
              </span>
            ) : (
              <span className="font-mono text-body">{painel?.versao_atual ?? 'Nenhuma'}</span>
            )}
            {painel?.atualizado_em ? (
              <span className="text-micro font-normal text-on-surface-muted">
                desde {formatarDataHora(painel.atualizado_em)}
              </span>
            ) : null}
          </div>
          <ArrowRight className="size-5 shrink-0 text-icon" aria-label="passa a ser" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="cmp-eyebrow">Nova</span>
            <span className="font-mono text-body font-bold text-primary">{versao}</span>
          </div>
        </div>

        {erro ? <Callout tom="erro">{erro}</Callout> : null}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={enviando}
            disabled={carregando || !versao}
            onClick={() => void confirmar()}
          >
            Publicar {versao}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
